import type { ResultadoHibrido } from '@via-tax/contracts'
import { reais } from '../formatos'

/**
 * Explicação ilustrada do Simples puro × híbrido para o cliente: o caminho de uma venda de R$ 10.000,00
 * a uma empresa do regime regular. Com simulação, usa os números da empresa; sem, só o desenho.
 * Responsiva: no celular a nota fica entre as duas empresas, com a seta para baixo.
 */

function Predio({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 28" className={`h-9 w-8 shrink-0 ${className}`} aria-hidden>
      <rect width="24" height="28" rx="1.5" fill="currentColor" />
      {[0, 1, 2].map((i) => [0, 1].map((j) => <rect key={`${i}${j}`} x={5 + j * 9} y={4 + i * 7} width="5" height="4" fill="#fff" />))}
      <rect x="9" y="21" width="6" height="7" fill="#fff" />
    </svg>
  )
}

function Seta() {
  return (
    <svg viewBox="0 0 40 12" className="h-3 w-10 shrink-0 rotate-90 text-acao sm:rotate-0" aria-hidden>
      <path d="M0 6h32" stroke="currentColor" strokeWidth="2.5" />
      <path d="M40 6l-9-5v10z" fill="currentColor" />
    </svg>
  )
}

type LinhaNota = { texto: string; destaque?: boolean; forte?: boolean }

function Fluxo(p: { titulo: string; cor: 'navy' | 'acao'; nota: LinhaNota[]; guias: string[]; empresa: string; cliente: string }) {
  const faixa = p.cor === 'navy' ? 'bg-navy' : 'bg-acao'
  return (
    <div className="overflow-hidden rounded-lg border border-linha bg-fundo">
      <p className={`${faixa} px-4 py-2 text-sm font-semibold text-white`}>{p.titulo}</p>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <Predio className="text-navy" />
            <span className="leading-tight">
              <span className="block text-sm font-semibold text-navy">Sua empresa</span>
              <span className="block text-[11px] text-tinta-3">Simples Nacional</span>
            </span>
          </div>
          <Seta />
          <div className="w-full max-w-[15rem] rounded-sm border border-navy bg-painel px-3 py-2 text-[12px] shadow-sm">
            {p.nota.map((l) => (
              <p key={l.texto} className={`${l.forte ? 'font-semibold' : ''} ${l.destaque ? 'font-semibold text-acao' : ''}`}>
                {l.texto}
              </p>
            ))}
          </div>
          <Seta />
          <div className="flex items-center gap-2 self-end sm:self-auto">
            <Predio className="text-tinta-3" />
            <span className="leading-tight">
              <span className="block text-sm font-semibold text-navy">Cliente empresa</span>
              <span className="block text-[11px] text-tinta-3">regime regular</span>
            </span>
          </div>
        </div>
        <div className="grid gap-3 text-[13px] sm:grid-cols-2">
          <div>
            <p className="text-[11px] text-tinta-3">Guias que a empresa paga</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {p.guias.map((g) => (
                <span key={g} className={`${faixa} rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-white`}>
                  {g}
                </span>
              ))}
            </div>
            <p className="mt-2 text-tinta-2">{p.empresa}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-[11px] text-tinta-3">O que acontece com o cliente</p>
            <p className="mt-1 text-tinta-2">{p.cliente}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

const REGRAS: Array<{ lado: 'híbrido' | 'puro' | 'atenção'; titulo: string; porque: string }> = [
  { lado: 'híbrido', titulo: 'Vende principalmente para empresas do regime regular', porque: 'elas recuperam o IBS/CBS da nota e a sua empresa paga menos DAS' },
  { lado: 'puro', titulo: 'Vende principalmente para consumidor final ou empresas do Simples', porque: 'eles não recuperam o tributo por fora, que acaba pesando no preço' },
  { lado: 'híbrido', titulo: 'Compra muito de fornecedores do regime regular', porque: 'só no híbrido o IBS/CBS das compras vira crédito' },
  { lado: 'atenção', titulo: 'Rotina mais trabalhosa no híbrido', porque: 'apurar CBS e IBS todo mês e destacar na nota' },
]

export function ComoFuncionaHibrido({ h }: { h: ResultadoHibrido | null }) {
  const ex = h?.exemploPreco?.find((g) => g.grupo === 'COM_CREDITO')
  const hoje = ex?.hoje
  const hib = ex?.hibrido[0]
  const agora = new Date().toISOString().slice(0, 7)
  const semestre = h?.opcao.semestres.find((x) => x.mesOpcao >= agora)
  return (
    <details open className="group rounded-lg border border-linha bg-painel">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-3">
        <span>
          <span className="block text-sm font-semibold">Como funciona: Simples puro × Simples híbrido</span>
          <span className="block text-xs text-tinta-3">Uma venda de R$ 10.000,00 a uma empresa do regime regular{ex ? ', com os números desta empresa' : ''}</span>
        </span>
        <span className="text-xs text-navy group-open:hidden">mostrar</span>
        <span className="hidden text-xs text-navy group-open:inline">ocultar</span>
      </summary>
      <div className="flex flex-col gap-4 border-t border-linha px-5 py-4">
        <Fluxo
          titulo="Simples puro — como é hoje"
          cor="navy"
          nota={[{ texto: 'NOTA FISCAL', forte: true }, { texto: `Valor: ${reais(hoje?.nota ?? 1_000_000)}`, forte: true }, { texto: 'CBS e IBS dentro do preço' }]}
          guias={[`DAS${hoje ? ` ${reais(hoje.das)}` : ''}`]}
          empresa="Uma guia só: o DAS reúne IRPJ, CSLL, CPP, ICMS/ISS e também a CBS e o IBS. As compras não geram crédito."
          cliente={`Aproveita de crédito só a parte de CBS/IBS que está dentro do DAS${hoje ? ` (${reais(hoje.creditoCliente)})` : ''}.`}
        />
        <Fluxo
          titulo="Simples híbrido — CBS e IBS por fora do DAS"
          cor="acao"
          nota={[
            { texto: 'NOTA FISCAL', forte: true },
            { texto: `Produto: ${reais(hib?.preco ?? 1_000_000)}` },
            { texto: `+ IBS/CBS destacado${hib ? `: ${reais(hib.tributo)}` : ''}`, destaque: true },
            ...(hib ? [{ texto: `Total: ${reais(hib.nota)}`, forte: true }] : []),
          ]}
          guias={[`DAS menor${hib ? ` ${reais(hib.das)}` : ''}`, 'CBS', 'IBS']}
          empresa="O DAS fica sem a parte de CBS/IBS. A CBS e o IBS são apurados todo mês: débito das vendas menos o crédito das compras."
          cliente={`Aproveita de crédito todo o IBS/CBS destacado${hib ? ` (${reais(hib.creditoCliente)}); o custo real dele fica em ${reais(hib.custoCliente)}, igual a hoje, com o preço do produto ajustado` : ''}.`}
        />
        <div>
          <p className="mb-2 text-sm font-semibold">Quando cada um tende a compensar</p>
          <ul className="flex flex-col gap-2">
            {REGRAS.map((r) => (
              <li key={r.titulo} className="flex items-start gap-2 text-[13px]">
                <span
                  className={`mt-0.5 w-16 shrink-0 rounded-full py-0.5 text-center text-[10px] font-semibold uppercase text-white ${r.lado === 'híbrido' ? 'bg-acao' : r.lado === 'puro' ? 'bg-navy' : 'bg-tinta-3'}`}
                >
                  {r.lado}
                </span>
                <span>
                  <strong>{r.titulo}</strong> <span className="text-tinta-2">— {r.porque}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        {semestre && (
          <p className="rounded-md border-l-4 border-acao bg-acao/10 px-3 py-2 text-[13px]">
            <strong>A escolha não é para sempre:</strong> opção em {semestre.mesOpcao.slice(5, 7)}/{semestre.mesOpcao.slice(0, 4)}
            {semestre.cancelamentoAte ? `, cancelável até ${semestre.cancelamentoAte.split('-').reverse().join('/')}` : ''}. Depois vale até a empresa renunciar, em março ou setembro.
          </p>
        )}
      </div>
    </details>
  )
}
